import { SIDEBAR_COOKIE_MAX_AGE, SIDEBAR_COOKIE_NAME } from '@/shared/config/sidebar';
import { create } from 'zustand';

/**
 * Client UI state for the app sidebar. Lives in a Zustand store (per the
 * project's "stores live in a `stores/` segment" rule) rather than React
 * context: the app has a single sidebar, so a global store avoids a provider
 * value and lets any component subscribe to just the slice it needs.
 *
 * Only the desktop `open` flag lives here (expanded/collapsed, persisted to a
 * cookie so it survives reloads). The mobile primary-nav overlay is the app-wide
 * `mobile-nav` dialog (dialog registry), so it needs no store flag. The fixed
 * layout options (side/variant/collapsible) are constants in
 * `src/shared/config/sidebar.ts`, `isMobile` is derived from `useIsMobile`, and
 * both are combined with this store in `useSidebar()`.
 */

/** Read the persisted desktop open state; defaults to collapsed (v1 `defaultOpen={false}`). */
function readInitialOpen(): boolean {
  if (typeof document === 'undefined') return false;
  const entry = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${SIDEBAR_COOKIE_NAME}=`));
  return entry ? entry.split('=')[1] !== 'false' : false;
}

/** Persist the desktop open state so it survives reloads. */
function persistOpen(open: boolean): void {
  if (typeof document === 'undefined') return;
  document.cookie = `${SIDEBAR_COOKIE_NAME}=${open}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`;
}

interface SidebarState {
  /** Desktop rail expanded (`true`) or collapsed to icons (`false`). */
  open: boolean;
  setOpen: (open: boolean) => void;
  toggleOpen: () => void;
}

export const useSidebarStore = create<SidebarState>((set, get) => ({
  open: readInitialOpen(),
  setOpen: (open) => {
    persistOpen(open);
    set({ open });
  },
  toggleOpen: () => {
    const open = !get().open;
    persistOpen(open);
    set({ open });
  },
}));
